import PropTypes from 'prop-types';
// @mui
import { Box } from '@mui/material';
// the vocabulary
import { ICONS } from './iconRegistry';

// ---------------------------------------------------------------------------
// THE ONE WAY TO DRAW AN ICON (DESIGN.md §10): Iconsax, one family, at a token
// size, in currentColor. `name` is the Iconsax export name (PascalCase, e.g.
// "SearchNormal1") and must be in the registry. The default variant is Linear
// (the stroked set); Bold is the emphasis form — an active tab, a pipeline
// stage, a selected rail item. An icon-only control names what it does with
// `label`, which becomes the accessible name; a decorative glyph beside a word
// is hidden from assistive technology.
// ---------------------------------------------------------------------------

const SIZES = { xs: 14, sm: 16, md: 20, lg: 24 };
// Iconsax draws its stroked variants with a 1.5 line on a 24px grid, which
// would scale down to a hairline at 16px. The line is made absolute (it does
// not scale with the glyph), so a 16px icon keeps the same firm line as a
// 24px one — a scaled, thin icon is what read as weak in the first pass.
const STROKES = { xs: 1.6, sm: 1.75, md: 1.75, lg: 1.75 };
const STROKED = ['Linear', 'Broken', 'TwoTone'];

/**
 * A GLYPH WHOSE SOLID FORM IS A FILL, not the `Bold` variant. Iconsax's Bold
 * star is cut through by a diagonal slash, which at 13px reads as a star
 * struck out — the opposite of what a rating means (the owner, 08/10/2026:
 * "I don't get why there is a cross over the star icon"; again on the
 * interpreters roster, 09/10/2026). Filling the LINEAR path gives the clean
 * solid shape, and hollow and solid stay the SAME glyph.
 */
export const SOLID_GLYPH_SX = { '& path': { fill: 'currentColor' } };

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.oneOfType([
    PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
    PropTypes.number
  ]),
  variant: PropTypes.oneOf([
    'Linear',
    'Outline',
    'Broken',
    'Bold',
    'Bulk',
    'TwoTone'
  ]),
  strokeWidth: PropTypes.number,
  /** Draw the outline FILLED instead of asking Iconsax for its Bold form. */
  solid: PropTypes.bool,
  label: PropTypes.string,
  sx: PropTypes.object
};

export default function Icon({
  name,
  size = 'sm',
  variant = 'Linear',
  strokeWidth,
  solid = false,
  label,
  sx,
  ...other
}) {
  const Glyph = ICONS[name];
  if (!Glyph) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `Icon: "${name}" is not in the registry (src/components/universe/iconRegistry.js)`
      );
    }
    return null;
  }
  // `solid` is drawn from the outline, so it never reaches for a Bold form.
  const drawn = solid ? 'Linear' : variant;
  const px = typeof size === 'number' ? size : SIZES[size] || SIZES.sm;
  const stroke =
    strokeWidth ??
    (typeof size === 'number'
      ? px <= 16
        ? STROKES.sm
        : STROKES.md
      : STROKES[size] || STROKES.md);
  return (
    <Box
      component={Glyph}
      size={px}
      variant={drawn}
      color="currentColor"
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      focusable="false"
      sx={{
        flex: 'none',
        display: 'inline-block',
        verticalAlign: 'middle',
        ...(STROKED.includes(drawn) && {
          '& path, & line, & circle, & rect, & polyline': {
            vectorEffect: 'non-scaling-stroke',
            strokeWidth: stroke
          }
        }),
        ...(solid && SOLID_GLYPH_SX),
        ...sx
      }}
      {...other}
    />
  );
}
